import React, { useEffect } from 'react'
import { Route, Routes, useLocation, useParams } from 'react-router-dom'
import { SiteDataProvider, useSiteData } from './context/SiteDataContext.jsx'
import { SiteHeader, SiteFooter } from './components/layout.jsx'
import { HomePage } from './pages/HomePage.jsx'
import { NewsPage } from './pages/NewsPage.jsx'
import { StaticCmsPage } from './pages/StaticCmsPage.jsx'
import { PatientStoriesPage } from './pages/PatientStoriesPage.jsx'
import { PatientStoryPage } from './pages/PatientStoryPage.jsx'
import { GalleryPage } from './pages/GalleryPage.jsx'
import { ForbiddenPage } from './pages/ForbiddenPage.jsx'
import { NotFoundPage } from './pages/NotFoundPage.jsx'
import { Container, Card } from './components/atoms.jsx'
import { PageHero, SeoManager, RichContent } from './components/content.jsx'

function ScrollRestoration() {
  const { pathname } = useLocation()

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [pathname])

  return null
}

function LoadingState() {
  return (
    <main className="py-20 lg:py-28">
      <Container>
        <Card className="mx-auto max-w-2xl space-y-5 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-forest-700">Indlæser</p>
          <p className="font-serif text-3xl text-forest-900">Helseklinikken</p>
          <p className="text-stone-600">Vi henter CMS-indholdet…</p>
        </Card>
      </Container>
    </main>
  )
}

function CmsSlugResolver({ basePath = '' }) {
  const { slug } = useParams()
  return <StaticCmsPage slug={slug} basePath={basePath} />
}

function AppShell() {
  const { loading, error } = useSiteData()

  if (loading) return <LoadingState />

  return (
    <div className="min-h-screen bg-sand-50 text-stone-800 antialiased">
      <ScrollRestoration />
      <SiteHeader />
      {error ? (
        <main className="py-20 lg:py-28">
          <Container>
            <Card className="mx-auto max-w-3xl space-y-4 text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-terracotta-600">Fejl</p>
              <h1 className="font-serif text-3xl text-forest-900">Der opstod en fejl under indlæsningen</h1>
              <p className="text-stone-600">{error}</p>
            </Card>
          </Container>
        </main>
      ) : (
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/behandlinger" element={<StaticCmsPage slug="behandlinger" />} />
          <Route path="/akupunktur" element={<StaticCmsPage slug="akupunktur" />} />
          <Route path="/rygsmerter" element={<StaticCmsPage slug="rygsmerter" />} />
          <Route path="/kol" element={<StaticCmsPage slug="kol" />} />
          <Route path="/sportsskader" element={<StaticCmsPage slug="sportsskader" />} />
          <Route path="/behandlingsmetoder" element={<StaticCmsPage slug="behandlingsmetoder" />} />
          <Route path="/behandlingsmetoder/:slug" element={<CmsSlugResolver basePath="behandlingsmetoder/" />} />
          <Route path="/sygdomme-lidelser" element={<StaticCmsPage slug="sygdomme-lidelser" />} />
          <Route path="/sygdomme-lidelser/:slug" element={<CmsSlugResolver basePath="sygdomme-lidelser/" />} />
          <Route path="/patienthistorier" element={<PatientStoriesPage />} />
          <Route path="/patienthistorie/:slug" element={<PatientStoryPage />} />
          <Route path="/galleri" element={<GalleryPage />} />
          <Route path="/nyheder" element={<NewsPage />} />
          <Route path="/priser" element={<StaticCmsPage slug="priser" />} />
          <Route path="/kontakt" element={<StaticCmsPage slug="kontakt" />} />
          <Route path="/403" element={<ForbiddenPage />} />
          <Route path="/:slug" element={<CmsSlugResolver />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      )}
      <SiteFooter />
    </div>
  )
}

export default function App() {
  return (
    <SiteDataProvider>
      <AppShell />
    </SiteDataProvider>
  )
}
